<template>
  <div>
    <Navbar />
    <Breadcrumb />
    <div class="container-xl">
      <div class="text-container">
        <div class="title-area">
          <div class="d-flex">
            <div class="survey-type-badge btn-primary">
              <fa-icon name="copy" size="6x" />
            </div>
            <div>
              <h2 class="pl-2">Aggregated Reports {{ step }}</h2>
            </div>
          </div>
        </div>
        <b-card-body>
          <div v-if="report_id == 0">
            <div v-if="step == 1">
              <b-row>
                <div class="col-12 mb-2">
                  <h4>Step 1 - Set report information</h4>
                </div>
                <div class="col-12 mb-2">
                  <b-alert variant="warning" show>
                    <b>Please note:</b> All underlying surveys on which the
                    aggregated report is based must have the same norm data.
                  </b-alert>
                </div>
                <b-form-group label="Report name" class="col-12">
                  <b-form-input
                    :class="`${anon ? 'g-anon' : ''}`"
                    v-model="report_name"
                  />
                </b-form-group>
                <b-form-group label="Survey type" class="col-12">
                  <b-form-select
                    v-model="surveytype_id"
                    :options="surveytypes"
                    @change="
                      language_options_get();
                      normvalues_countries_get();
                    "
                  />
                </b-form-group>
                <b-form-group
                  label="Norm values to use for the report"
                  class="col-12"
                >
                  <b-form-select
                    v-model="normvalues_id"
                    :options="normvalues"
                  />
                </b-form-group>
                <b-form-group label="Report language" class="col-12">
                  <b-form-select v-model="language_id" :options="languages" />
                </b-form-group>
              </b-row>
            </div>
            <div v-if="step == 2">
              <b-row>
                <div class="col-12 mb-2">
                  <h4>Step 2 - Select Surveys</h4>
                  Check the box for the surveys you wish to include in the
                  report.
                </div>
                <div class="col-12 mb-2">
                  <h5>
                    Surveys (total: {{ surveyRows }}, selected:
                    {{ surveyselectcount }})
                  </h5>
                </div>
                <div v-show="consultantgroup_id != '0'">
                  <b-form-checkbox
                    v-model="showgroupsurveys"
                    class="ml-4"
                    @change="showconsultantsingroupsurveys"
                  >
                    Show surveys from other consultants in my group
                  </b-form-checkbox>
                </div>
                <b-alert
                  variant="danger"
                  v-model="shownormvalueserror"
                  class="col-12 mb-2"
                >
                  <b>Please note:</b> this survey does not have the same norm
                  data as the norm data selected for the report. If you include
                  this survey, the report will make incorrect comparisons.
                </b-alert>
                <div class="gh300 col-12">
                  <b-table
                    striped
                    responsive
                    hover
                    :items="surveys"
                    :fields="surveyfields"
                    :busy="loading"
                  >
                    <template #table-busy>
                      <div class="text-center text-success my-2">
                        <b-spinner class="align-middle mr-2" />
                        <strong>Loading...</strong>
                      </div>
                    </template>
                    <template v-slot:cell(group_id)="data">
                      <span :class="`${anon ? 'g-anon' : ''}`">{{
                        data.item.group_id
                      }}</span>
                    </template>
                    <template v-slot:cell(survey_name)="data">
                      <span :class="`${anon ? 'g-anon' : ''}`">{{
                        data.item.survey_name
                      }}</span>
                    </template>
                    <template v-slot:cell(id)="data">
                      <div class="py-1">
                        <b-form-checkbox
                          v-model="data.item.selected"
                          value="1"
                          unchecked-value="0"
                          @change="surveys_countselected"
                        />
                      </div>
                    </template>
                    <template v-slot:cell(user_id)="data">
                      <fa-icon
                        v-if="data.item._user_id !== consultant_id"
                        name="user"
                        :title="` ${data.item.user_id} (${data.item._user_id}) `"
                      />
                    </template>
                  </b-table>
                </div>
              </b-row>
            </div>
            <div v-if="step == 3">
              <b-row>
                <div class="col-12 mb-2">
                  <h4>Step 3 - Select measurement periods for comparison</h4>
                </div>
                <b-card
                  class="drop-zone col-12 px-0 mx-3 my-3"
                  header-text-variant="white"
                  header-tag="header"
                  header-bg-variant="secondary"
                  title=""
                  style="max-width: 100rem"
                  header="Surveys selected for the aggregated report:"
                  @drop="onDrop($event, 0)"
                  @dragover.prevent
                  @dragenter.prevent
                >
                  <div
                    v-for="survey in surveys.filter(
                      (s) => s.in_timeslot != 1 && s.selected == 1
                    )"
                    :key="survey.id"
                    draggable
                    style="cursor: pointer"
                    @dragstart="startDrag($event, survey)"
                  >
                    <span class="ml-2">
                      {{ survey.survey_end_date }}
                      <span :class="`${anon ? 'g-anon' : ''}`">{{
                        survey.survey_name
                      }}</span>
                    </span>
                  </div>
                </b-card>
              </b-row>
              <b-row>
                <div class="col-12 mb-2 mx-2 my-2">
                  <h5>Add a measurement period:</h5>
                </div>
              </b-row>
              <b-row>
                <b-form-group class="col-7 pl-4" label="Label:" label-cols="3">
                  <b-input
                    id="dateperiod_label"
                    v-model="dateperiod_label"
                    inline
                    type="text"
                    input-class="form-control"
                  />
                </b-form-group>
                <b-form-group class="col-4" inline>
                  <button
                    class="btn btn-primary"
                    :class="dateperiod_label == '' ? 'disabled' : ''"
                    :disabled="dateperiod_label == ''"
                    @click="adddateperiod"
                  >
                    <fa-icon name="check" />
                    Add
                  </button>
                </b-form-group>
              </b-row>
              <b-row>
                <div class="col-12 mb-2 mx-2">
                  <h5>Measurement periods for the report:</h5>
                </div>
              </b-row>
              <b-row class="mb-4">
                <div v-if="dateperiods.length == 0" class="col-12 mb-2 mx-2">
                  No measurement periods have been added
                </div>
                <b-card
                  v-for="dateperiod in dateperiods"
                  :key="dateperiod.id"
                  class="mx-3"
                  header-text-variant="white"
                  header-tag="header"
                  header-bg-variant="primary"
                  title=""
                  style="max-width: 40rem"
                >
                  <template #header>
                    {{ dateperiod.label }}
                    <span
                      class="right pull-right text-right right-align float-right"
                      @click="removedateperiod(dateperiod.id)"
                    >
                      <fa-icon name="trash" />
                    </span>
                  </template>
                  <b-card-body
                    class="drop-zone"
                    @drop="onDrop($event, dateperiod.id)"
                    @dragover.prevent
                    @dragenter.prevent
                  >
                    <div
                      v-for="survey in dateperiod.surveys"
                      :key="survey.id"
                      draggable
                      style="cursor: pointer"
                      @dragstart="startDrag($event, survey)"
                    >
                      {{ survey.survey_end_date }}
                      <span :class="`${anon ? 'g-anon' : ''}`">{{
                        survey.survey_name
                      }}</span>
                    </div>
                    <div v-if="dateperiod.surveys.length == 0">
                      Drag and drop surveys here from the list above
                    </div>
                  </b-card-body>
                </b-card>
              </b-row>
            </div>
          </div>
          <div v-if="invoice_id === 0">
            <h4 v-if="step == 4">Step 4 - Summary</h4>
            <div v-if="step == 4">
              <b-alert variant="warning" show>
                <b>Please note:</b> after you have completed the payment in the
                next step, you can no longer make any changes to the report.
                Please review the summary information below before proceeding to
                payment. If everything is correct, click &quot;Next&quot;.
              </b-alert>
              <b-row class="mb-4">
                <div class="col-12 mb-2">
                  <h5>Report parameters</h5>
                </div>
                <div class="col-12">
                  You have made the following selections for the report:
                </div>
                <div class="col-3">Report name:</div>
                <div class="col-9">
                  <span :class="`${anon ? 'g-anon' : ''}`">{{
                    report_name
                  }}</span>
                </div>
                <div class="col-3">Survey type:</div>
                <div class="col-9">
                  {{
                    surveytypes.filter((s) => s.value == surveytype_id)[0].text
                  }}
                </div>
                <div class="col-3">Norm values to use for the report:</div>
                <div class="col-9">
                  {{
                    normvalues.filter((s) => s.value == normvalues_id)[0].text
                  }}
                </div>
                <div class="col-3">Report language:</div>
                <div class="col-9">
                  {{ languages.filter((s) => s.value == language_id)[0].text }}
                </div>
              </b-row>
              <b-row class="mb-4">
                <div class="col-12 mb-2">
                  <h5>Measurement periods</h5>
                </div>
                <div v-if="dateperiods.length == 0" class="col-12 mb-2 mx-2">
                  No measurement periods have been added
                </div>
                <b-card
                  v-for="dateperiod in dateperiods"
                  :key="dateperiod.id"
                  class="mx-4"
                  header-text-variant="white"
                  header-tag="header"
                  header-bg-variant="primary"
                  title=""
                  style="max-width: 40rem"
                >
                  <template #header>
                    {{ dateperiod.label }}
                  </template>
                  <b-card-body>
                    <div v-for="survey in dateperiod.surveys" :key="survey.id">
                      {{ survey.survey_end_date }}
                      <span :class="`${anon ? 'g-anon' : ''}`">{{
                        survey.survey_name
                      }}</span>
                    </div>
                    <div v-if="dateperiod.surveys.length == 0">
                      No available surveys completed in this period
                    </div>
                  </b-card-body>
                </b-card>
              </b-row>
            </div>
          </div>
          <!-- Payment -->
          <div v-if="step == 5">
            <div v-if="invoice_id != 0 && invoice_status != 2">
              <h4>Report Invoice</h4>
              <b-table
                id="invoiceheadtable"
                :items="invoicehead"
                :fields="invoiceheadfields"
                thead-class="hidden_header"
              >
                <template v-slot:cell(value)="data">
                  <span :class="`${anon ? 'g-anon' : ''}`">
                    {{ data.item.value }}
                  </span>
                </template>
              </b-table>
              <b-table
                id="invoicedetailstable"
                :items="invoicedetails"
                :fields="invoicedetailsfields"
                foot-clone
              >
                <template v-slot:cell(item_text)="data">
                  <span :class="`${anon ? 'g-anon' : ''}`">
                    {{ data.item.item_text }}
                  </span>
                </template>
                <template v-slot:foot(item_text)>
                  TOTAL SUM<br />
                  <strong>VAT</strong><br />
                  <strong>TOTAL SUM INCLUDING VAT</strong>
                </template>
                <template v-slot:foot(price)>
                  {{ invoice.invoice_amount }} SEK<br />
                  <strong>{{ invoice.invoice_vat_amount }} SEK</strong><br />
                  <strong>{{ invoice.invoice_amount_inc_vat }} SEK</strong>
                </template>
              </b-table>
              <div class="justify-content-center text-center d-flex pb-2">
                <strong>Contact information:&nbsp;&nbsp;</strong>GDQ Associates
                AB, e-mail:&nbsp;&nbsp;<a href="mailto: gdq@gdq.se"
                  >gdq@gdq.se</a
                >, telephone: +46 76 052 52 03
              </div>
              <div class="justify-content-center text-center d-flex pb-2">
                <a :href="config.links.privacyPolicy"
                  >Click here to view our privacy policy</a
                >
              </div>
              <h4>Payment</h4>
              <h5>Invoice payee information</h5>
              <b-form-group label-cols="4">
                <template v-slot:label>
                  Invoice Reference
                  <b>
                    <span v-if="invoice_reference == ''" class="text-danger">
                      required
                    </span>
                  </b>
                </template>
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_reference"
                  placeholder="Invoice reference"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4">
                <template v-slot:label>
                  Invoice Name
                  <b
                    ><span v-if="invoice_name == ''" class="text-danger"
                      >required</span
                    ></b
                  >
                </template>
                <b-form-input
                  v-model="invoice_name"
                  :class="`${anon ? 'g-anon' : ''}`"
                  placeholder="Invoice name"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4" label="E-mail">
                <b-form-input
                  v-model="invoice_email"
                  :class="`${anon ? 'g-anon' : ''}`"
                  placeholder="Invoice e-mail"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4">
                <template v-slot:label>
                  Company
                  <b
                    ><span
                      :class="`${anon ? 'g-anon' : ''}`"
                      v-if="invoice_company == ''"
                      class="text-danger"
                      >required</span
                    ></b
                  >
                </template>
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_company"
                  placeholder="Company name"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4" label="Address">
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_address_1"
                  placeholder="Invoice address line 1"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4" label="">
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_address_2"
                  placeholder="Invoice address line 2"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4" label="Zip code">
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_zip_code"
                  size="6"
                  maxlength="6"
                  class="col-3"
                  placeholder="Zip code"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group label-cols="4" label="City">
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_city"
                  placeholder="City"
                  @keyup="invoice_updateinfo"
                />
              </b-form-group>
              <b-form-group
                label-cols="4"
                description="Fill out the VAT number (organisationsnummer) if the invoice should be issued to an organisation. If you wish to receive the invoice as a private individual, fill out your social security number (personnummer). "
              >
                <template v-slot:label>
                  VAT number/social security number
                  <b>
                    <span
                      v-if="invoice_company_regno == ''"
                      class="text-danger"
                    >
                      required
                    </span>
                  </b>
                </template>
                <b-form-input
                  :class="`${anon ? 'g-anon' : ''}`"
                  v-model="invoice_company_regno"
                  placeholder="VAT number/social security number"
                  @keyup="invoice_updateinfo"
                />
                <p></p>
              </b-form-group>
              <b-form-group>
                <b-form-checkbox v-model="invoice_accept" class="pb-4">
                  <strong>
                    I accept the
                    <a href="https://gdq.se/sv/kopvillkor/" target="_new">
                      terms of purchase
                    </a>
                    and the charges as specified above
                  </strong>
                </b-form-checkbox>
              </b-form-group>
              <b-form-group
                v-if="
                  invoice_accept &&
                  invoice_name != '' &&
                  invoice_company != '' &&
                  invoice_company_regno != '' &&
                  invoice_reference != ''
                "
                label="I want to pay by:"
              >
                <b-form-radio-group stacked>
                  <!-- b-form-radio
                    v-model="payment"
                    name="payment"
                    value="1"
                  >
                    Credit Card
                  </b-form-radio -->
                  <b-form-radio v-model="payment" name="payment" value="2">
                    Invoice
                  </b-form-radio>
                  <b-form-radio v-model="payment" name="payment" value="4">
                    Credit Card
                  </b-form-radio>
                </b-form-radio-group>
              </b-form-group>
              <!-- Invoice payment -->
              <div v-if="payment == 2">
                <b-form-group>
                  <b-form-label>I want to receive my invoice by:</b-form-label>
                  <b-form-select
                    v-model="e_invoice"
                    :options="einvoiceoptions"
                    @change="invoice_updateinfo"
                  />
                </b-form-group>
                The invoice will be sent to the address you have provided. If
                you already have a link to e-invoices via your organisation, the
                invoice will be sent via that route. As a part of our
                environmental management, we encourage digital invoices. Ensure
                that you have entered your e-mail if you want to receive the
                invoice by e-mail.
                <b-form-group class="text-right align-right mt-4 pt-4">
                  <b-button
                    :disabled="formok == false"
                    size="lg"
                    variant="primary"
                    @click.prevent="invoice_save"
                  >
                    <span class="icon-file-presentation mr-2 pl-2" />Continue to
                    the Report
                  </b-button>
                </b-form-group>
              </div>
              <!-- DIBS payment -->
              <div v-if="payment == 1">
                <h5>Card Payment</h5>
                <div class="py-4 col-12 text-center align-center">
                  Payment service provided by DIBS<br />
                  <img
                    class="ml-4"
                    src="https://cdn.dibspayment.com/logo/checkout/combo/horiz/DIBS_checkout_kombo_horizontal_04.png"
                    alt="DIBS - Payments made easy"
                    width="270"
                  />
                </div>
                <form
                  id="gdq_dibsform"
                  ref="gdq_dibsform"
                  class="text-center align-center"
                  method="post"
                  action="https://payment.architrade.com/paymentweb/start.action"
                >
                  <input
                    type="hidden"
                    name="amount"
                    :value="invoice.dibs_amount * 100"
                  />
                  <input type="hidden" name="currency" value="SEK" />
                  <input type="hidden" name="lang" value="en" />
                  <input type="hidden" name="capturenow" value="yes" />
                  <input type="hidden" name="decorator" value="responsive" />
                  <input type="hidden" name="merchant" value="90219297" />
                  <input type="hidden" name="orderid" :value="order_id" />
                  <input
                    type="hidden"
                    name="ordertext"
                    :value="invoice.invoice_text"
                  />
                  <input type="hidden" name="accepturl" :value="accept_url" />
                  <input
                    type="hidden"
                    name="callbackurl"
                    :value="callback_url"
                  />
                  <input type="hidden" name="cancelurl" :value="cancel_url" />
                  <input type="hidden" name="gdqurl" :value="gdq_url" />
                  <input
                    v-if="consultant_id == 1"
                    label="test"
                    type="text"
                    name="test"
                    value="test"
                  />
                  <b-button
                    size="lg"
                    variant="primary"
                    @click.prevent.stop="dibs_payment"
                  >
                    <span class="icon-receipt mr-2 pl-2" />Continue to DIBS
                    Payment
                  </b-button>
                </form>
              </div>
              <!-- NETS Easy Payment -->
              <div v-if="payment == 4">
                <NetsPayment
                  product-name="GDQ Aggregated Report"
                  :dibs-amount="invoice.dibs_amount"
                  :order-id="order_id"
                  :accept-url="web_url"
                  :cancel-url="web_url"
                />
              </div>
            </div>
            <div
              v-if="invoice_id != 0 && invoice_status == 2"
              class="mx-2 px-2 align-center text-center center"
            >
              <h3 class="mb-4 pb-4">
                <span v-if="report_loaded == 0 || processingpayment == true">
                  <b-spinner
                    style="width: 2rem; height: 2rem"
                    type="grow"
                    variant="primary"
                  />
                  Generating your report...
                </span>
                <span v-if="report_loaded == 1 && processingpayment == false"
                  >Thank you! Your aggregated report has been generated and will
                  appear in the list &quot;My aggregated reports&quot; on the
                  home page.</span
                >
              </h3>
              <b-row>
                <b-button
                  v-if="report_loaded == 1"
                  class="mx-auto large col-4 mb-4"
                  variant="primary"
                  @click.prevent="report_download"
                >
                  Download report
                </b-button>
              </b-row>
              <b-row>
                <b-button
                  v-if="report_loaded == 1"
                  class="mx-auto large col-4"
                  variant="warning"
                  @click.prevent="receipt_download"
                >
                  Download payment receipt
                </b-button>
              </b-row>
            </div>
          </div>
          <b-row v-if="step < 5 && processingpayment == false">
            <div class="col-6 pl-0 pt-4 mt-4">
              <b-button
                class="float-left mx-3"
                variant="secondary"
                @click="prevstep"
              >
                &lt; {{ step == 1 ? "Cancel" : "Back" }}
              </b-button>
            </div>
            <div class="col-6 pr-0 pt-4 mt-4">
              <b-button
                class="float-right"
                variant="primary"
                :disabled="stepnotcomplete()"
                @click="nextstep"
              >
                Next >
              </b-button>
            </div>
          </b-row>
        </b-card-body>
      </div>
    </div>
  </div>
</template>
<script>
// invoice_status:
// 0 = ?
// 1 =
// 2 =

import Navbar from './Navbar.vue'
import Breadcrumb from './Breadcrumb.vue'
import NetsPayment from './NetsPayment'
import { config } from '../../config'
import axios from 'axios'

const surveyfields = [
  { key: 'id', label: ' ', sortable: false, tdClass: 'w-2' },
  { key: 'user_id', label: '', sortable: true, tdClass: 'w-1' },
  {
    key: 'group_id',
    sortable: true,
    label: 'Group name',
    tdClass: 'd-table-cell',
    thClass: 'd-table-cell'
  },
  {
    key: 'survey_name',
    sortable: true,
    label: 'Survey name',
    tdClass: 'd-table-cell',
    thClass: 'd-table-cell'
  },
  {
    key: 'survey_end_date',
    sortable: true,
    label: 'Completion date',
    tdClass: 'd-table-cell',
    thClass: 'd-table-cell'
  },
  {
    key: 'membercount',
    sortable: true,
    label: 'Questionnaires',
    tdClass: 'd-table-cell',
    thClass: 'd-table-cell'
  },
  {
    key: 'completecount',
    sortable: true,
    label: 'Replied',
    tdClass: 'd-table-cell',
    thClass: 'd-table-cell'
  }
]

export default {
  name: 'AggregatedReports',
  components: {
    Navbar,
    Breadcrumb,
    NetsPayment
  },
  data () {
    return {
      i_email: this.$store.state.user.i_email,
      profile: this.$store.state.profile,
      consultant_id: this.$store.state.user_id,
      step: 1,
      groupRows: 0,
      surveyRows: 0,
      report_id: this.$store.state.currentaggregatedreport_id,
      report_name: '',
      surveytypes: [],
      languages: [],
      normvalues: [],
      surveys: [],
      surveyfields: surveyfields,
      surveytype_id: 0,
      language_id: 0,
      normvalues_id: 0,
      surveyselectcount: 0,
      select_start_date: '',
      select_end_date: '',
      dateperiod_label: '',
      dateperiods: [],
      order_id: 0,
      invoice_id: 0,
      invoice_paid: 0,
      invoice_status: 0,
      invoicehead: [],
      invoiceheadfields: [
        { key: 'label', label: '' },
        { key: 'value', label: '', tdClass: 'align-right text-right' }
      ],
      invoicedetails: [],
      invoicedetailsfields: [
        { key: 'item_text', label: 'Report name', sortable: false },
        {
          key: 'price',
          label: 'Price',
          sortable: false,
          thClass: 'align-right text-right',
          tdClass: 'align-right text-right'
        }
      ],
      invoice_reference: '',
      invoice_name: '',
      invoice_email: '',
      invoice_company: '',
      invoice_address_1: '',
      invoice_address_2: '',
      invoice_zip_code: '',
      invoice_city: '',
      invoice_company_regno: '',
      e_invoice: 0,
      einvoiceoptions: [
        { value: '0', text: 'Paper invoice sent by post' },
        { value: '1', text: 'PDF invoice sent by e-mail' },
        { value: '2', text: 'E-invoice' }
      ],
      invoice_accept: false,
      formok: false,
      payment: null,
      base_url: config.base_url,
      web_url: window.location + '',
      report_loaded: 0,
      netspayment_id: this.$route.query.paymentid
        ? this.$route.query.paymentid
        : '',
      nets_paid_amount: 0,
      nets_invoice_amount: 0,
      processingpayment: !!this.$route.query.paymentid,
      consultantgroup_id: this.$store.state.profile.consultantgroup_id,
      consultantsingroupids: [],
      showgroupsurveys: false,
      shownormvalueserror: false,
      loading: false
    }
  },
  computed: {
    anon: function () {
      return this.$store.state.anon
    },
    accept_url: function () {
      var url = config.report_url + '/gdq_dibsreturn.php'
      // var url = window.location;
      return url
    },
    cancel_url: function () {
      var url = config.report_url + '/gdq_dibsreturn.php'
      return url
    },
    callback_url: function () {
      var url = this.base_url + '/gdq_dcallback'
      return url
    },
    gdq_url: function () {
      var url = window.location
      return url
    }
  },
  async created () {
    await this.checknetspayment()
    await this.profile_get()
    this.surveytypes_get()
    this.normvalues_countries_get()
    this.language_options_get()
    this.report_get()
    this.consultantsingroup_get()
  },
  methods: {
    async profile_get () {
      const res = await this.$store.dispatch('profile_fetch', {})
      this.consultantgroup_id = res.data.consultantgroup_id
      return res
    },
    async showconsultantsingroupsurveys () {
      await this.consultantsingroup_get()
      this.surveys_get()
    },
    consultantsingroup_get () {
      let consultantsingroup = [this.$store.state.profile]
      this.consultantsingroupids = [this.consultant_id]

      if (this.consultantgroup_id != '0' && this.showgroupsurveys === true) {
        const filter = {
          consultantgroup_id: {
            operator: '=',
            value: this.consultantgroup_id
          }
        }
        return this.$store
          .dispatch('records_readall', { table_name: 'g_profile', filter })
          .then((resp) => {
            consultantsingroup = resp.data.result
            this.consultantsingroupids = []
            for (let i = 0; i < consultantsingroup.length; i++) {
              this.consultantsingroupids.push(consultantsingroup[i].id)
            }
          })
      } else {
        return true
      }
    },
    async checknetspayment () {
      if (this.netspayment_id != '') {
        this.step = 5
        this.processingpayment = true
        // Update the invoice and payment_lof
        await this.$store
          .dispatch('netspayment_update', { paymentId: this.netspayment_id })
          .then(async (resp) => {
            if (resp.data.status == 'OK') {
              // Remove paymentID
              await this.makereport().then(() => {
                this.$router.push(this.$route.path)
              })
            } else {
              alert(resp.data.error)
            }
            this.processingpayment = false
          })
      }
    },
    startDrag (evt, survey) {
      evt.dataTransfer.dropEffect = 'move'
      evt.dataTransfer.effectAllowed = 'move'
      evt.dataTransfer.setData('surveyID', survey.id)
    },
    onDrop (evt, list) {
      const surveyID = evt.dataTransfer.getData('surveyID')
      let survey = this.surveys.find(
        (survey) => survey.id == surveyID && survey.selected == 1
      )
      delete survey.group_id

      if (list !== 0) {
        let dateperiod = this.dateperiods.find(
          (dateperiod) => dateperiod.id == list
        )
        dateperiod.surveys.push(survey)
        survey.in_timeslot = 1
      }
      let index = null
      let period = null
      this.dateperiods.forEach((p) => {
        let i = p.surveys.filter((s) => {
          return s.id.toString() == surveyID.toString()
        })
        if (i.length > 0 && p.id != list) {
          period = p
          index = i
        }
      })
      if (index && period) {
        period.surveys.splice(index, 1)
      }
      if (list === 0) {
        survey.in_timeslot = 0
      }
    },
    dibs_payment () {
      var payload = { invoice_id: this.invoice_id }
      this.$store.dispatch('paymentlog_create', payload).then((resp) => {
        // ---------------------
        // Here we POST to DIBS
        // ---------------------
        this.$refs.gdq_dibsform.submit()
      })
    },
    // Create or get the invoice
    // ----------------------------------------------------------------
    // Get/create invoice for report
    // invoice_status:
    // 0 = There are invoices to be paid/handled
    // 1 = Not used
    // 2 = All invoices created and payment selected
    // report_paid:
    // 0 = We have not done the payment processing to end
    // 1 = The payment processing is finished (inv or return from DIBS)
    // report_loaded:
    // 0 = The report has not been loaded
    // 1 = The report has been loaded
    // ----------------------------------------------------------------
    invoice_get () {
      // This on returns an existing invoice or a newly created one
      // invoice_paid = 0 - Newly created invoice or unpaid previous invoice
      // invoice_paid = 1 - Hunky dory
      this.$store
        .dispatch('aggregatedinvoice_fetch', { report_id: this.report_id })
        .then((resp) => {
          this.invoice_id = resp.data.invoice_id
          this.invoice = resp.data.invoice
          this.invoice_paid = resp.data.invoice_paid
          this.order_id = 1000000 + parseInt(this.invoice_id)
          this.invoice_status = resp.data.invoice.invoice_status
          this.invoicedetails = resp.data.invoice.invoicedetails
          this.invoicehead.push({
            label: 'Invoice Number',
            value: resp.data.invoice.invoice_number
          })
          this.invoicehead.push({
            label: 'Invoice Date',
            value: resp.data.invoice.invoice_date
          })
          this.invoicehead.push({
            label: 'Invoice Reference',
            value: resp.data.invoice.invoice_reference
          })
          this.invoice['dibs_amount'] =
            this.invoice.invoice_amount_inc_vat.replace(',', '')
        })
    },
    // Save the invoice information
    invoice_save () {
      var invoice = {
        invoice_reference: this.invoice_reference,
        invoice_name: this.invoice_name,
        invoice_company: this.invoice_company,
        invoice_address_1: this.invoice_address_1,
        invoice_address_2: this.invoice_address_2,
        invoice_zip_code: this.invoice_zip_code,
        invoice_city: this.invoice_city,
        invoice_status: 2,
        invoice_payment_method_id: this.payment,
        invoice_company_regno: this.invoice_company_regno,
        e_invoice: this.e_invoice,
        invoice_email: this.invoice_email
        // coupon_id: this.coupon_id,
      }
      this.$store
        .dispatch('groupsurveyinvoice_save', {
          invoice_id: this.invoice_id,
          invoice: invoice
        })
        .then((resp) => {
          this.report_paid = 1
          this.invoice_status = 2
          this.$nextTick(() => {
            // this.$store.dispatch('groupsurveyinvoice_notifyadmin', { invoice_id: this.invoice_id })
            this.invoice_paid = 1
            this.makereport()
          })
        })
    },
    invoice_updateinfo () {
      var invoice = {
        invoice_reference: this.invoice_reference,
        invoice_name: this.invoice_name,
        invoice_company: this.invoice_company,
        invoice_address_1: this.invoice_address_1,
        invoice_address_2: this.invoice_address_2,
        invoice_zip_code: this.invoice_zip_code,
        invoice_city: this.invoice_city,
        invoice_company_regno: this.invoice_company_regno,
        e_invoice: this.e_invoice,
        invoice_email: this.invoice_email
      }
      this.$store
        .dispatch('groupsurveyinvoice_save', {
          invoice_id: this.invoice_id,
          invoice: invoice
        })
        .then((resp) => {
          this.checkform()
        })
    },
    receipt_download () {
      this.$store
        .dispatch('gdqagreport_receipt_create', { invoice_id: this.invoice_id })
        .then((resp) => {
          var byteCharacters = atob(resp.data)
          var byteNumbers = new Array(byteCharacters.length)
          for (var i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          var byteArray = new Uint8Array(byteNumbers)
          var blob = new Blob([byteArray], { type: 'application/pdf' })
          var fileURL = window.URL.createObjectURL(blob)
          var fileLink = document.createElement('a')
          fileLink.href = fileURL
          fileLink.setAttribute(
            'download',
            'GDQ_Receipt_' +
              this.invoice_id +
              '_' +
              new Date().toISOString().split('T')[0]
          )
          document.body.appendChild(fileLink)
          fileLink.click()
        })
    },
    // Get the report from state and DB
    report_get () {
      // We are accessing a previously created report
      if (this.report_id != 0) {
        this.$store
          .dispatch('record_read', {
            table_name: 'g_agreports',
            id: this.report_id
          })
          .then((resp) => {
            this.report_name = resp.data.report_name
            this.surveytype_id = resp.data.surveytype_id
            this.normvalues_id = resp.data.normvalues_id
            this.language_id = resp.data.language_id
            const reportsel = resp.data.report_selections
            const sel = JSON.parse(reportsel)
            this.dateperiods = sel.dateperiods
            this.invoice_get()
            this.step = 5
            this.report_loaded = 1
          })
      }
    },
    // ----------------------------
    // Create the report in the DB
    // ----------------------------
    savereport () {
      const data = {
        report_name: this.report_name,
        surveytype_id: this.surveytype_id,
        normvalues_id: this.normvalues_id,
        language_id: this.language_id,
        dateperiods: this.dateperiods,
        report_active: 0
      }
      this.$store.dispatch('gdqagreport_save', data).then((res) => {
        this.report_id = res.data.id
        this.$store.dispatch('setcurrentaggregatedreport_id', {
          report_id: this.report_id
        })
        this.invoice_get()
      })
    },
    checkform () {
      this.formok = true
      if (this.invoice_name == '') {
        this.formok = false
      }
      if (this.invoice_company == '') {
        this.formok = false
      }
      if (this.invoice_company_regno == '') {
        this.formok = false
      }
      if (this.invoice_accept == false) {
        this.formok = false
      }
      if (this.invoice_reference == '') {
        this.formok = false
      }
    },
    // -------------------------------------------------------
    // Creates the report PowerPoint and updates the database
    // -------------------------------------------------------
    async makereport () {
      const data = {
        report_id: this.report_id
      }
      let action = 'gdqagreport_create'
      if (this.surveytype_id == 2) {
        action = 'gdqagshortreport_create'
      }
      return await this.$store.dispatch(action, data).then((resp) => {
        this.report_id = resp.data.id
        this.report_paid = 1
        this.report_loaded = 1
      })
    },
    report_download () {
      var reporturl =
        config.report_url + '/gdq_agreport.php?r=' + this.report_id
      var downloadURL = new URL(reporturl)
      var downloadLink = document.createElement('a')
      downloadLink.href = downloadURL
      downloadLink.setAttribute(
        'download',
        'GDQ_AGReport_' + new Date().toISOString().split('T')[0]
      )
      document.body.appendChild(downloadLink)
      downloadLink.click()
    },
    stepnotcomplete () {
      if (
        (this.step == 1 &&
          (this.surveytype_id == 0 ||
            this.normvalues_id == 0 ||
            this.language_id == 0)) ||
        (this.step == 2 && this.surveyselectcount == 0) ||
        (this.step == 3 && this.dateperiods.length == 0)
      ) {
        return true
      }
      return false
    },
    adddateperiod () {
      if (this.dateperiod_label != '') {
        const period = {
          id: this.dateperiods.length + 1,
          label: this.dateperiod_label,
          surveys: []
        }
        this.dateperiods.push(period)
        this.dateperiod_label = ''
      }
    },
    removedateperiod (id) {
      const period = this.dateperiods.filter((p) => p.id == id)[0]
      period.surveys.forEach((s) => {
        s.in_timeslot = 0
      })
      const index = this.dateperiods.map((e) => e.id).indexOf(id)
      this.dateperiods.splice(index, 1)
    },
    marktimeslottedsurveys (start_date, end_date) {
      this.surveys.forEach((s) => {
        if (
          s.survey_end_date >= start_date &&
          s.survey_end_date <= end_date &&
          s.selected == 1
        ) {
          s.in_timeslot = 1
        }
      })
    },
    surveytypes_get () {
      this.surveytypes = []
      let filtertypes = []
      filtertypes.push(2)
      filtertypes.push(1)
      const filtervalue = filtertypes.toString()
      const filter = {
        id: {
          operator: 'IN',
          value: filtervalue
        }
      }
      this.$store
        .dispatch('records_readall', {
          table_name: 'g_surveytypes',
          filter: filter,
          orderby: 'surveytype_name'
        })
        .then((resp) => {
          this.surveytypes = resp.data.result.map((r) => {
            return { value: r.id, text: r.surveytype_name }
          })
          if (this.surveytypes.length === 1) {
            this.surveytype_id = this.surveytypes[0].value
          }
        })
    },
    normvalues_countries_get: function () {
      this.$store.dispatch('normvalues_countries_fetch').then((resp) => {
        this.normvalues = resp.data
          .map((n) => {
            return {
              value: n.country_id,
              text: n.country_name
            }
          })
          .filter((n) => {
            return n.value == '999' || this.surveytype_id == 1
          })
        if (this.normvalues.length === 1) {
          this.normvalues_id = this.normvalues[0].value
        }
      })
    },
    language_options_get () {
      var filter = {}
      switch (this.surveytype_id) {
        case '1':
          filter['language_enabled'] = {
            operator: '=',
            value: '1'
          }
          break
        case '2':
          filter['language_enabled_gdq_short'] = {
            operator: '=',
            value: '1'
          }
          break
        case '3':
          filter['language_enabled_osc'] = {
            operator: '=',
            value: '1'
          }
          break
        default:
          filter['language_enabled'] = {
            operator: '=',
            value: '1'
          }
      }
      this.$store
        .dispatch('records_readall', {
          table_name: 'g_languages',
          filter: filter,
          orderby: 'language_name'
        })
        .then((resp) => {
          this.languages = resp.data.result
            .map((l) => {
              return {
                value: l.id,
                text: l.language_name
              }
            })
            .filter((n) => {
              return (
                n.value == '154' || n.value == '38' || this.surveytype_id == 1
              )
            })
          if (this.languages.length === 1) {
            this.language_id = this.languages[0].value
          }
        })
      return true
    },
    surveys_get () {
      this.loading = true
      let filter = {
        user_id: {
          operator: 'IN',
          value: this.consultantsingroupids.join(','),
          realfield: 1
        },
        survey_status_id: {
          operator: '==',
          value: 4
        },
        survey_deleted: {
          operator: '==',
          value: 0
        },
        survey_type_id: {
          operator: '==',
          value: this.surveytype_id
        }
      }
      axios.defaults.headers.common['Authorization'] =
        'Bearer:' + this.$store.state.token
      axios
        .get(
          config.base_url + '/gdq_surveys_get?filter=' + JSON.stringify(filter)
        )
        .then((resp) => {
          this.surveys = resp.data.result.map((s) => {
            s.selected = 0
            s.in_timeslot = 0
            return s
          })
          this.surveyRows = this.surveys.length
          this.loading = false
        })
    },
    surveys_countselected () {
      this.$nextTick(() => {
        var surveyselectcount = 0
        this.shownormvalueserror = false
        for (var i = 0; i < this.surveys.length; i++) {
          if (
            this.surveys[i].survey_normvalues_id != this.normvalues_id &&
            this.surveys[i].selected == 1
          ) {
            this.surveys[i].selected = 0
            this.shownormvalueserror = true
          }
          if (this.surveys[i].selected == 1) {
            surveyselectcount++
          }
        }
        this.surveyselectcount = surveyselectcount
      })
    },
    prevstep () {
      this.step--
      if (this.step == 0) {
        this.$router.push('/')
      }
    },
    nextstep () {
      this.step++
      if (this.step == 2) {
        this.surveys_get()
      }
      if (this.step == 3) {
        const thesurveys = this.surveys.filter((s) => s.selected == 1)
        const min = thesurveys.reduce(function (prev, current) {
          return prev.survey_end_date < current.survey_end_date
            ? prev
            : current
        })
        const max = thesurveys.reduce(function (prev, current) {
          return prev.survey_end_date > current.survey_end_date
            ? prev
            : current
        })
        this.select_start_date = min.survey_start_date
        this.select_end_date = max.survey_end_date
      }
      if (this.step == 5) {
        this.savereport()
      }
    }
  }
}
</script>
<style scoped>
.gh400 {
  height: 400px !important;
  overflow-y: scroll !important;
}
.gh300 {
  height: 300px !important;
  overflow-y: scroll !important;
}
.line-through {
  text-decoration: line-through;
}
</style>
